'use client';

import { useState } from 'react';
import Link from 'next/link';
import { AlertTriangle, X } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { useBudgetStatus } from '@/hooks/use-budget-alerts';
import { cn } from '@/lib/utils';

export function BudgetAlertBanner() {
  const { user } = useAuth();
  const { data: statuses } = useBudgetStatus();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  // Only render for admin users
  if (user?.role !== 'admin') return null;
  if (!statuses) return null;

  // Find statuses that need alerts (>= 80% or exceeded)
  const alerts = statuses.filter(
    (s) =>
      (s.percentage_used >= 80 || s.is_exceeded) &&
      !dismissed.has(s.period)
  );

  if (alerts.length === 0) return null;

  return (
    <div className="space-y-1 mb-4">
      {alerts.map((status) => {
        const isCritical = status.is_exceeded;
        const hasBlock = status.active_alerts.some(
          (a) => a.action_on_exceed === 'block'
        );

        return (
          <div
            key={status.period}
            className={cn(
              'flex items-center justify-between rounded-lg px-4 py-2 text-sm',
              isCritical
                ? 'bg-red-500/15 text-red-400 border border-red-500/30'
                : 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
            )}
          >
            <Link
              href="/admin/budget"
              className="flex items-center gap-2 hover:underline"
            >
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              {isCritical ? (
                <span>
                  Бюджет превышен! ${status.current_spend_usd.toFixed(2)} / $
                  {status.limit_usd.toFixed(2)} ({status.period})
                  {hasBlock && ' (LLM вызовы заблокированы)'}
                </span>
              ) : (
                <span>
                  Расход бюджета: {status.percentage_used.toFixed(1)}% (
                  {status.period})
                </span>
              )}
            </Link>
            <button
              onClick={() =>
                setDismissed((prev) => new Set([...prev, status.period]))
              }
              className="rounded p-1 hover:bg-white/10"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
